/**
 * A credit note as a page, `/credit-notes/:creditNoteNo` (docs/features/
 * order-to-invoice/50_finance_sales_ledger_module_spec.md §9 item 3): the
 * invoice it credits, finance's reason, the lines credited, where the money
 * went and how each refund stands.
 *
 * No mock-up was drawn for this page: it is the smallest page consistent with
 * the order page's Invoice step. Finance's reason is shown here and nowhere the
 * customer reads: their credit note and portal entry list the credited lines
 * only (Ben, 6 Oct). Every word of a state is the backend's.
 */
import { Link, useParams } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import {
  DocumentPageStatus,
  MoneySummary,
  Section,
  StateBadge,
  headerFacts,
  useDocumentPage,
  usePdfDownload,
} from "../app/components/domain/sales-ledger/DocumentParts";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import { Button } from "../app/components/primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

export function CreditNotePage() {
  const { creditNoteNo: rawNo } = useParams<{ creditNoteNo: string }>();
  const creditNoteNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const { state } = useDocumentPage(
    () => adapter.getCreditNotePage(creditNoteNo),
    [adapter, creditNoteNo],
  );
  const pdf = usePdfDownload();

  if (state.kind !== "loaded")
    return (
      <DocumentPageStatus
        state={state}
        what="credit note"
        documentNo={creditNoteNo}
      />
    );
  const { credit_note: note, header, lines, refunds, reason } = state.page;

  return (
    <div className="space-y-6">
      <ObjectHeader
        crumbs={[
          { label: "Customers", to: paths.customers() },
          {
            label: header.customer_name ?? header.customer_code,
            to: paths.customerDetail({ customerId: header.customer_code }),
          },
          {
            label: "Invoices",
            to: paths.customerInvoices({ customerId: header.customer_code }),
          },
          { label: note.credit_note_no },
        ]}
        title={`Credit note ${note.credit_note_no}`}
        pills={<StateBadge state={note.state} label={note.state_label} />}
        value={formatQuotedPrice(-note.gross_pence)}
        valueLabel="VAT included"
        facts={[
          ...headerFacts(header),
          {
            label: "Credits",
            value: (
              <Link
                to={paths.invoiceDetail({ invoiceNo: note.invoice_no })}
                className="hover:underline"
              >
                {note.invoice_no}
              </Link>
            ),
          },
          { label: "Issued", value: formatShortDateOrDash(note.issued_on) },
          { label: "Money", value: note.refund_label },
        ]}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={pdf.busy}
              onClick={() => void pdf.viewPdf(note.pdf_url)}
            >
              View PDF
            </Button>
            {note.xero_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => capabilities.openExternalUrl(note.xero_url!)}
              >
                Open in Xero
              </Button>
            )}
          </>
        }
      />

      {pdf.notice && (
        <p role="status" className="text-muted-foreground text-sm">
          {pdf.notice}
        </p>
      )}

      <Section title="Reason">
        <p className="text-sm">{reason}</p>
        <p className="text-muted-foreground text-xs">
          Internal: the customer&apos;s credit note lists the lines credited and
          never says why.
        </p>
      </Section>

      <Section title="Lines credited">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Line</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="hidden text-right md:table-cell">
                VAT
              </TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead>
                <span className="sr-only">In full or in part</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.line_no}>
                <TableCell>
                  <span className="text-muted-foreground tabular-nums">
                    {line.line_no}
                  </span>{" "}
                  {line.description}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.quantity.toLocaleString("en-GB")}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatQuotedPrice(-line.net_pence)}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums md:table-cell">
                  {formatQuotedPrice(-line.vat_pence)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatQuotedPrice(-line.gross_pence)}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {line.is_full ? "In full" : "Part of the line"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <MoneySummary
          ariaLabel="Credit note total"
          rows={[
            { label: "Net", pence: -note.net_pence, muted: true },
            { label: "VAT", pence: -note.vat_pence, muted: true },
            { label: "Gross", pence: -note.gross_pence, emphasis: true },
          ]}
        />
      </Section>

      <Section title="Refunds">
        {refunds.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {note.refund_label}: nothing is paid back. Any credit left stays on
            the customer&apos;s account in Xero.
          </p>
        ) : (
          <ul className="divide-y rounded-md border text-sm">
            {refunds.map((r, i) => (
              <li
                key={`${r.method}-${i}`}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2"
              >
                <span className="flex flex-wrap items-center gap-2">
                  {r.method_label}
                  <StateBadge
                    state={
                      r.status === "made"
                        ? "refunded"
                        : r.status === "failed"
                          ? "refund_failed"
                          : "refund_pending"
                    }
                    label={r.status_label}
                  />
                  {r.reference && (
                    <span className="text-muted-foreground">{r.reference}</span>
                  )}
                  {r.refunded_on && (
                    <span className="text-muted-foreground">
                      {formatShortDateOrDash(r.refunded_on)}
                    </span>
                  )}
                </span>
                <span className="tabular-nums">
                  {formatQuotedPrice(r.amount_pence)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
